/**
 * vizPivotGenerator.js - Pivot table configuration parser and code generator
 *
 * Config shape:
 *   { xAxis: { slug, field, dateGroup? }, series: { slug, field } | null,
 *     measures: [{ slug, field, agg, label? }], formatting: { chartType?, palette?, title?, ... } }
 *
 * The magic comment /*@pivot {...} *\/ is prepended to generated code so
 * parsePivotConfig() can round-trip the config from stored js_content.
 */

// ─── Color palettes ──────────────────────────────────────────────────────────

export const COLOR_PALETTES = {
  default:    ['#6366f1','#f59e0b','#10b981','#ef4444','#3b82f6','#8b5cf6','#ec4899','#14b8a6'],
  pastel:     ['#a5b4fc','#fcd34d','#6ee7b7','#fca5a5','#93c5fd','#c4b5fd','#f9a8d4','#5eead4'],
  bold:       ['#4338ca','#d97706','#059669','#dc2626','#2563eb','#7c3aed','#db2777','#0d9488'],
  earth:      ['#92400e','#78350f','#065f46','#1e3a5f','#3d2c1e','#44403c','#4b3f2f','#6b4c11'],
  monochrome: ['#1f2937','#374151','#4b5563','#6b7280','#9ca3af','#d1d5db','#e5e7eb','#f3f4f6'],
};

// ─── Type helpers ─────────────────────────────────────────────────────────────

const NUMERIC_TYPES = new Set(['integer','bigint','double','float','decimal','numeric','real','int','int4','int8','float4','float8','hugeint','ubigint','uinteger','smallint','tinyint']);
const DATE_TYPES    = new Set(['date','timestamp','timestamp with time zone','timestamptz','datetime']);

export function isNumeric(type) {
  return NUMERIC_TYPES.has((type || '').toLowerCase().split('(')[0].trim());
}

export function isDate(type) {
  return DATE_TYPES.has((type || '').toLowerCase().trim());
}

// ─── Config parser ────────────────────────────────────────────────────────────

/** Returns parsed pivot config object if js_content starts with /*@pivot…*\/, else null. */
export function parsePivotConfig(jsContent) {
  if (!jsContent) return null;
  const match = jsContent.match(/^\/\*@pivot\s*([\s\S]*?)\*\//);
  if (!match) return null;
  try {
    return JSON.parse(match[1].trim());
  } catch {
    return null;
  }
}

// ─── Code generator ───────────────────────────────────────────────────────────

function formatDateGroup(fieldExpr, dateGroup) {
  switch (dateGroup) {
    case 'day':     return `String(${fieldExpr}).substring(0, 10)`;
    case 'week':    return `(d => { const t = new Date(${fieldExpr}); t.setDate(t.getDate() - t.getDay()); return t.toISOString().substring(0,10); })()`;
    case 'month':   return `String(${fieldExpr}).substring(0, 7)`;
    case 'quarter': return `(d => { const t = new Date(${fieldExpr}); return t.getFullYear() + '-Q' + (Math.floor(t.getMonth()/3)+1); })()`;
    case 'year':    return `String(new Date(${fieldExpr}).getFullYear())`;
    default:        return fieldExpr;
  }
}

function aggExpr(agg, field, accKey) {
  switch (agg) {
    case 'sum':   return `acc.${accKey} += (parseFloat(r["${field}"]) || 0);`;
    case 'avg':   return `acc.${accKey} += (parseFloat(r["${field}"]) || 0); acc.__n_${accKey} = (acc.__n_${accKey} || 0) + 1;`;
    case 'min':   return `{ const v = parseFloat(r["${field}"]); if (!isNaN(v)) acc.${accKey} = acc.${accKey} === undefined ? v : Math.min(acc.${accKey}, v); }`;
    case 'max':   return `{ const v = parseFloat(r["${field}"]); if (!isNaN(v)) acc.${accKey} = acc.${accKey} === undefined ? v : Math.max(acc.${accKey}, v); }`;
    case 'count': return `acc.${accKey} = (acc.${accKey} || 0) + 1;`;
    default:      return `acc.${accKey} = r["${field}"];`;
  }
}

function aggFinalize(agg, accKey) {
  if (agg === 'avg') return `(g.__n_${accKey} ? g.${accKey} / g.__n_${accKey} : 0)`;
  return `(g.${accKey} || 0)`;
}

/** Generates JS viz code (with /*@pivot…*\/ prefix) from a pivot config object. */
export function generatePivotCode(config) {
  const { xAxis, series, measures = [], formatting = {} } = config || {};

  const configJson = JSON.stringify(config, null, 2);
  const header = `/*@pivot\n${configJson}\n*/\n`;

  if (!xAxis || measures.length === 0) {
    return header + `return { data: [], layout: { title: "Configure X Axis and at least one Measure" } };`;
  }

  const xSlug    = xAxis.slug;
  const xField   = xAxis.field;
  const xGroup   = xAxis.dateGroup || null;
  const chartType = formatting.chartType || 'bar';
  const palette   = COLOR_PALETTES[formatting.palette] || COLOR_PALETTES.default;
  const title     = formatting.title || '';

  // Build accumulator key per measure
  const metas = measures.map((m, i) => ({
    ...m,
    accKey: `m${i}_${m.agg}_${m.field.replace(/[^a-zA-Z0-9]/g, '_')}`,
    label: m.label || `${m.agg}(${m.field})`,
  }));

  const xValueExpr = xGroup
    ? formatDateGroup(`r["${xField}"]`, xGroup)
    : `String(r["${xField}"] ?? "")`;

  const seriesField = series?.field;
  const seriesSlug  = series?.slug;

  // All slugs we need rows from
  const slugs = [...new Set([xSlug, ...(seriesSlug ? [seriesSlug] : []), ...metas.map(m => m.slug)])];

  // Join rows by matching the primary slug rows; other slugs are accessed by index (same length assumed)
  const rowsLines = slugs.map((s, i) => `  const rows${i === 0 ? '' : '_' + s} = viewData["${s}"] || [];`).join('\n');
  const primaryRows = 'rows';

  const accInits = metas.map(m => `${m.accKey}: 0`).join(', ');
  const accUpdates = metas.map(m => `        ${aggExpr(m.agg, m.field, m.accKey)}`).join('\n');

  let code = header;
  code += `${rowsLines}\n\n`;

  if (series && seriesSlug) {
    // Grouped: { [xVal]: { [seriesVal]: { m0: ..., m1: ... } } }
    code += `const agg = {};\n`;
    code += `const seriesVals = new Set();\n`;
    code += `${primaryRows}.forEach(r => {\n`;
    code += `  const xv = ${xValueExpr};\n`;
    code += `  const sv = String(r["${seriesField}"] ?? "");\n`;
    code += `  if (!agg[xv]) agg[xv] = {};\n`;
    code += `  if (!agg[xv][sv]) agg[xv][sv] = { ${accInits} };\n`;
    code += `  const acc = agg[xv][sv];\n`;
    code += `${accUpdates}\n`;
    code += `  seriesVals.add(sv);\n`;
    code += `});\n\n`;
    code += `const xKeys = Object.keys(agg).sort();\n`;
    code += `const svArr = [...seriesVals].sort();\n\n`;

    const plotlyType = chartType === 'pie' ? 'bar' : (chartType.startsWith('area') ? 'scatter' : chartType === 'scatter' ? 'scatter' : 'bar');
    const m = metas[0]; // grouped charts show first measure per series

    code += `const data = svArr.map((sv, si) => ({\n`;
    code += `  type: "${plotlyType}",\n`;
    if (plotlyType === 'scatter') {
      code += `  mode: "lines${chartType === 'scatter' ? '+markers' : ''}",\n`;
      if (chartType.startsWith('area')) code += `  fill: "${chartType === 'area_pct' ? 'tonexty' : 'tozeroy'}",\n`;
    }
    code += `  x: xKeys,\n`;
    code += `  y: xKeys.map(xv => agg[xv]?.[sv] ? ${aggFinalize(m.agg, m.accKey)} : 0),\n`.replace('g.', 'agg[xv][sv].').replace('(g.__n_', '(agg[xv][sv].__n_'),
    code += `  name: sv,\n`;
    code += `  marker: { color: ${JSON.stringify(palette)}[si % ${palette.length}] },\n`;
    code += `}));\n\n`;
    code += `const layout = {\n`;
    if (title) code += `  title: "${title}",\n`;
    code += `  barmode: "${chartType === 'area_pct' ? 'stack' : 'group'}",\n`;
    if (formatting.xTitle) code += `  xaxis: { title: "${formatting.xTitle}" },\n`;
    if (formatting.yTitle) code += `  yaxis: { title: "${formatting.yTitle}" },\n`;
    code += `  legend: { orientation: "h" },\n`;
    code += `};\n`;
    code += `return { data, layout };\n`;
  } else if (chartType === 'pie' && metas.length >= 1) {
    const m = metas[0];
    code += `const agg = {};\n`;
    code += `${primaryRows}.forEach(r => {\n`;
    code += `  const xv = ${xValueExpr};\n`;
    code += `  if (!agg[xv]) agg[xv] = { ${accInits} };\n`;
    code += `  const acc = agg[xv];\n`;
    code += `${accUpdates}\n`;
    code += `});\n\n`;
    code += `const labels = Object.keys(agg).sort();\n`;
    code += `const values = labels.map(xv => { const g = agg[xv]; return ${aggFinalize(m.agg, m.accKey)}; });\n\n`;
    code += `const data = [{ type: "pie", labels, values, marker: { colors: ${JSON.stringify(palette)} } }];\n`;
    code += `const layout = {${ title ? ` title: "${title}",` : '' } };\n`;
    code += `return { data, layout };\n`;
  } else {
    // One trace per measure
    code += `const agg = {};\n`;
    code += `${primaryRows}.forEach(r => {\n`;
    code += `  const xv = ${xValueExpr};\n`;
    code += `  if (!agg[xv]) agg[xv] = { ${accInits} };\n`;
    code += `  const acc = agg[xv];\n`;
    code += `${accUpdates}\n`;
    code += `});\n\n`;
    code += `const xKeys = Object.keys(agg).sort();\n\n`;

    const plotlyType = chartType.startsWith('area') ? 'scatter' : chartType === 'scatter' ? 'scatter' : 'bar';

    code += `const data = [\n`;
    metas.forEach((m, si) => {
      code += `  { type: "${plotlyType}",`;
      if (plotlyType === 'scatter') {
        code += ` mode: "lines${chartType === 'scatter' ? '+markers' : ''}",`;
        if (chartType.startsWith('area')) code += ` fill: "${chartType === 'area_pct' ? 'tonexty' : 'tozeroy'}",`;
      }
      code += ` x: xKeys, y: xKeys.map(xv => { const g = agg[xv]; return ${aggFinalize(m.agg, m.accKey)}; }),`;
      code += ` name: "${m.label}", marker: { color: "${palette[si % palette.length]}" } },\n`;
    });
    code += `];\n\n`;
    code += `const layout = {\n`;
    if (title) code += `  title: "${title}",\n`;
    if (formatting.xTitle) code += `  xaxis: { title: "${formatting.xTitle}" },\n`;
    if (formatting.yTitle) code += `  yaxis: { title: "${formatting.yTitle}" },\n`;
    if (metas.length > 1) code += `  barmode: "group",\n`;
    code += `};\n`;
    code += `return { data, layout };\n`;
  }

  return code;
}
